import React, { useState, useEffect } from 'react';
import { getUserProfile, updateProfilePicture, completeUserProfile } from '../services/auth';
import DefaultAvatar from '../assets/default-avatar.png'; // You'll need to add this image

const ProfileManagement = ({ userId, onClose }) => {
  const [username, setUsername] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profilePicture, setProfilePicture] = useState('');
  const [previewImage, setPreviewImage] = useState(DefaultAvatar);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('picture'); // 'picture' or 'password'

  // Fetch user profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        const profile = await getUserProfile(userId);
        
        if (profile) {
          setUsername(profile.username || '');
          if (profile.profilePicture) {
            setProfilePicture(profile.profilePicture);
            setPreviewImage(profile.profilePicture);
          }
        }
        
        setProfileLoaded(true);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setError('Failed to load profile data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [userId]);

  // Handle file upload for profile picture
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Profile picture must be less than 5MB');
      return;
    }

    // Check file type (allow only images)
    if (!file.type.startsWith('image/')) {
      setError('File must be an image');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setPreviewImage(event.target.result);
      setProfilePicture(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Handle updating profile picture
  const handleUpdatePicture = async () => {
    if (!profilePicture) {
      setError('Please select a profile picture');
      return;
    }
    
    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await updateProfilePicture(userId, profilePicture);
      setSuccess('Profile picture updated successfully');
    } catch (error) {
      setError(error.message || 'Failed to update profile picture');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async () => {
    setError('');
    setSuccess('');
    
    // Validate password fields
    if (!oldPassword) {
      setError('Current password is required');
      return;
    }
    
    if (!newPassword) {
      setError('New password is required');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Update password logic
      // This uses the same endpoint as completing profile, but
      // we need to validate the old password on the server
      await completeUserProfile(userId, {
        username, // Include current username to maintain it
        currentPassword: oldPassword,
        password: newPassword,
        profilePicture // Include current profile picture to maintain it
      });
      
      setSuccess('Password updated successfully');
      
      // Clear password fields
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setError(error.message || 'Failed to update password');
    } finally {
      setIsLoading(false);
    }
  };

  if (!profileLoaded) {
    return (
      <div className="profile-loading">
        <div className="spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-management-container">
      <div className="profile-header">
        <h2>Profile Settings</h2>
        <button className="close-profile-btn" onClick={onClose}>
          &times;
        </button>
      </div>
      
      {error && (
        <div className="profile-error">
          <p>{error}</p>
          <button onClick={() => setError('')} className="dismiss-error">
            Dismiss
          </button>
        </div>
      )}
      
      {success && (
        <div className="profile-success">
          <p>{success}</p>
          <button onClick={() => setSuccess('')} className="dismiss-success">
            Dismiss
          </button>
        </div>
      )}
      
      <div className="profile-tabs">
        <button 
          className={`profile-tab ${activeTab === 'picture' ? 'active' : ''}`}
          onClick={() => setActiveTab('picture')}
        >
          Profile Picture
        </button>
        <button 
          className={`profile-tab ${activeTab === 'password' ? 'active' : ''}`}
          onClick={() => setActiveTab('password')}
        >
          Change Password
        </button>
      </div>
      
      {/* Profile Picture Tab */}
      {activeTab === 'picture' && (
        <div className="profile-picture-section">
          <div className="current-user-info">
            <p className="username-display">@{username || 'Username'}</p>
          </div>
          
          <div className="profile-image-container">
            <img 
              src={previewImage} 
              alt="Profile" 
              className="profile-image-preview" 
            />
            <label htmlFor="profile-picture" className="change-picture-label">
              Change Picture
            </label>
          </div>
          
          <input
            type="file"
            id="profile-picture"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
            onChange={handleFileChange}
            className="file-input"
          />
          
          <button
            className="update-picture-btn"
            onClick={handleUpdatePicture}
            disabled={isLoading}
          >
            {isLoading ? 'Updating...' : 'Update Profile Picture'}
          </button>
        </div>
      )}
      
      {/* Password Change Tab */}
      {activeTab === 'password' && (
        <div className="password-change-section">
          <div className="form-group">
            <label htmlFor="current-password">Current Password*</label>
            <input
              type="password"
              id="current-password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              placeholder="Enter your current password"
              disabled={isLoading}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="new-password">New Password*</label>
            <input
              type="password"
              id="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Create a new password"
              disabled={isLoading}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="confirm-new-password">Confirm New Password*</label>
            <input
              type="password"
              id="confirm-new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new password"
              disabled={isLoading}
              required
            />
          </div>
          
          <button
            className="change-password-btn"
            onClick={handlePasswordChange}
            disabled={isLoading}
          >
            {isLoading ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfileManagement;